import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ConfirmAbsenceMessageDialogProps = {
  open: boolean;
  message: string;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const ConfirmAbsenceMessageDialog: React.FC<ConfirmAbsenceMessageDialogProps> = ({
  open,
  message,
  isPending = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <Dialog id="absence-message-confirm-dialog" open={open} onClose={onClose} maxWidth="sm">
      <CardHeader
        id="absence-message-confirm-header"
        title={t("pages.account.absenceMessage.confirmTitle")}
        onExit={onClose}
        variant="contained"
      />
      <CardContent className={styles.content}>
        <Typography variant="body">{t("pages.account.absenceMessage.confirmIntro")}</Typography>
        <Typography variant="body" className={styles.message}>
          {message}
        </Typography>
      </CardContent>
      <CardFooter className={styles.footer}>
        <Button variant="outlined" data-action="messaging.absence.cancel" onClick={onClose} disabled={isPending}>
          {t("button.cancel")}
        </Button>
        <Button variant="contained" data-action="messaging.absence.confirm" onClick={onConfirm} pending={isPending}>
          {t("pages.account.absenceMessage.confirm")}
        </Button>
      </CardFooter>
    </Dialog>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    paddingTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  message: css({
    whiteSpace: "pre-wrap",
    fontStyle: "italic",
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(8),
  }),
});
